import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, Shield } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

// Live assets
const LOGO_URL = "https://customer-assets.emergentagent.com/job_frog-ecodev/artifacts/3z4427yl_image.png";

const Header = () => {
  return (
    <header className="bg-white sticky top-0 z-50 shadow-sm border-t-4 border-secondary">
      <div className="container mx-auto px-4 h-24 flex items-center justify-between">
        
        <div className="flex items-center gap-4">
           <Link to="/" className="flex items-center gap-3">
             <img src={LOGO_URL} alt="Frog Lake First Nations" className="h-16 w-auto object-contain" />
           </Link>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8 text-sm font-sans font-bold uppercase tracking-wider text-zinc-800">
          <Link to="/" className="text-secondary transition-colors py-1" data-testid="nav-home">Home</Link>
          
          {/* About Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger className="hover:text-secondary transition-colors py-1 outline-none uppercase font-bold tracking-wider cursor-pointer" data-testid="nav-about">About</DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white border rounded-none shadow-md p-2 min-w-[150px]">
              <DropdownMenuItem asChild>
                <Link to="/about" className="w-full block p-2 hover:bg-gray-100">Our Nation</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/visitors" className="w-full block p-2 hover:bg-gray-100">Visitors</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Residents Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger className="hover:text-secondary transition-colors py-1 outline-none uppercase font-bold tracking-wider cursor-pointer" data-testid="nav-residents">Residents</DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white border rounded-none shadow-md p-2 min-w-[200px]">
              <DropdownMenuItem asChild>
                <Link to="/residents#emergency" className="w-full block p-2 hover:bg-gray-100">Emergency Services</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/residents#employment" className="w-full block p-2 hover:bg-gray-100">Employment</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/residents#facilities" className="w-full block p-2 hover:bg-gray-100">Facilities</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/residents#schools" className="w-full block p-2 hover:bg-gray-100">Schools</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/residents#recreation" className="w-full block p-2 hover:bg-gray-100">Recreation Centre</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Business Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger className="hover:text-secondary transition-colors py-1 outline-none uppercase font-bold tracking-wider cursor-pointer" data-testid="nav-business">Business</DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white border rounded-none shadow-md p-2 min-w-[200px]">
              <DropdownMenuItem asChild>
                <Link to="/business#airport" className="w-full block p-2 hover:bg-gray-100">Local Airport</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/business#directory" className="w-full block p-2 hover:bg-gray-100">Business Directory</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/business#investing" className="w-full block p-2 hover:bg-gray-100">Investing In Frog Lake</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/business#bids" className="w-full block p-2 hover:bg-gray-100">Bids & Tenders</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/business#payments" className="w-full block p-2 hover:bg-gray-100">Payment Methods</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/news" className="hover:text-secondary transition-colors py-1" data-testid="nav-events">Events</Link>
          <Link to="/partners" className="hover:text-secondary transition-colors py-1" data-testid="nav-partners">Partners</Link>
          <Link to="/proposal" className="hover:text-secondary transition-colors py-1" data-testid="nav-proposal">Proposal</Link>
          
          {/* Partner Invoicing */}
          <Button asChild variant="outline" className="font-bold uppercase tracking-widest ml-2 rounded-none border border-black/20" data-testid="nav-partner-invoicing">
            <Link to="/partner-invoicing">Partner Invoicing</Link>
          </Button>

          {/* Portal Login - Now goes to Auth0 */}
          <Button asChild className="bg-primary hover:bg-yellow-500 text-black font-bold uppercase tracking-widest ml-2 rounded-none border border-black/10" data-testid="nav-portal-login">
            <Link to="/portal">Portal Login</Link>
          </Button>
        </nav>

        {/* Mobile Menu */}
        <div className="lg:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="h-8 w-8" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="text-left font-heading uppercase text-[#D97706]">Menu</SheetTitle>
              </SheetHeader>
              
              <div className="flex flex-col gap-4 mt-6">
                <Link to="/" className="text-lg font-bold uppercase py-2 border-b">Home</Link>
                
                <Accordion type="single" collapsible className="w-full">
                  
                  {/* About */}
                  <AccordionItem value="about">
                    <AccordionTrigger className="text-lg font-bold uppercase">About</AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-3 pl-4">
                      <Link to="/about" className="py-2 text-gray-600 hover:text-primary">Our Nation</Link>
                      <Link to="/visitors" className="py-2 text-gray-600 hover:text-primary">Visitors</Link>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Residents */}
                  <AccordionItem value="residents">
                    <AccordionTrigger className="text-lg font-bold uppercase">Residents</AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-3 pl-4">
                      <Link to="/residents#emergency" className="py-2 text-gray-600 hover:text-primary">Emergency Services</Link>
                      <Link to="/residents#employment" className="py-2 text-gray-600 hover:text-primary">Employment</Link>
                      <Link to="/residents#facilities" className="py-2 text-gray-600 hover:text-primary">Facilities</Link>
                      <Link to="/residents#schools" className="py-2 text-gray-600 hover:text-primary">Schools</Link>
                      <Link to="/residents#recreation" className="py-2 text-gray-600 hover:text-primary">Recreation Centre</Link>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Business */}
                  <AccordionItem value="business">
                    <AccordionTrigger className="text-lg font-bold uppercase">Business</AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-3 pl-4">
                      <Link to="/business#airport" className="py-2 text-gray-600 hover:text-primary">Local Airport</Link>
                      <Link to="/business#directory" className="py-2 text-gray-600 hover:text-primary">Business Directory</Link>
                      <Link to="/business#investing" className="py-2 text-gray-600 hover:text-primary">Investing In Frog Lake</Link>
                      <Link to="/business#bids" className="py-2 text-gray-600 hover:text-primary">Bids & Tenders</Link>
                      <Link to="/business#payments" className="py-2 text-gray-600 hover:text-primary">Payment Methods</Link>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                <Link to="/news" className="text-lg font-bold uppercase py-2 border-b">Events</Link>
                <Link to="/partners" className="text-lg font-bold uppercase py-2 border-b">Partners</Link>
                <Link to="/proposal" className="text-lg font-bold uppercase py-2 border-b" data-testid="mobile-nav-proposal">Proposal</Link>
                
                <div className="mt-4 space-y-3">
                  <Button asChild className="w-full bg-primary hover:bg-yellow-500 text-black font-bold uppercase tracking-widest rounded-none">
                    <Link to="/portal">Portal Login</Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full font-bold uppercase tracking-widest rounded-none">
                    <Link to="/partner-invoicing">Partner Invoicing</Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full font-bold uppercase tracking-widest rounded-none text-xs">
                    <Link to="/portal/admin-login">
                      <Shield className="mr-2 h-3 w-3" />
                      Admin Access
                    </Link>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
